import React, { useContext, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Button, GridLegacy as Grid, Stack, Typography } from '@mui/material';

import { AccommodationPropertyDetailsContext } from '~/components/Accommodation/Pages/PropertyDetails/context/AccommodationPropertyDetailsContext';
import useAccommodationPropertyDetailsContext from '~/components/Accommodation/Pages/PropertyDetails/context/useAccommodationPropertyDetailsContext';
import GenerateContentModal, { InitiateContentGenerationRequest } from '~/components/Common/GenerateContentModal';
import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_ADMIN_USER, ROLE_COORDINATOR, ROLE_HOTEL_COORDINATOR } from '~/consts/roles';

import { getPropertyContent, initiateOfferContentGeneration } from '~/services/AccommodationService';

import AccommodationGeneratedContentView from './AccommodationGeneratedContentView';

const CONTENT_ROLES = [ROLE_ADMIN_USER, ROLE_COORDINATOR, ROLE_HOTEL_COORDINATOR];

export default function AccommodationPropertyGeneratedContent() {
  const { enqueueSnackbar } = useSnackbar();
  const { propertyDetails, propertyContent } = useAccommodationPropertyDetailsContext();
  const { setPropertyContent } = useContext(AccommodationPropertyDetailsContext);
  const [useWebSearch, setUseWebSearch] = useState(false);
  const [contentTypeToGenerate, setContentTypeToGenerate] = useState<InitiateContentGenerationRequest['contentType']>();
  const [isGenerateContentModalOpen, setIsGenerateContentModalOpen] = useState(false);

  useEffect(() => {
    setUseWebSearch(contentTypeToGenerate === 'nearby');
  }, [contentTypeToGenerate]);

  const hasGeneratedContent = () => propertyContent?.generatedContents.length > 0;
  const connectedOffer = useMemo(
    () => propertyDetails.connectedOffers.find((offer) => offer.idSalesforceExternal && offer.offerType !== 'Bedbank'),
    [propertyDetails.connectedOffers],
  );
  const canGenerateContent = Boolean(connectedOffer?.idSalesforceExternal);

  const fetchContent = async () => {
    try {
      const response = await getPropertyContent({ propertyId: propertyDetails.id });
      if (response.result && setPropertyContent) {
        setPropertyContent(response.result);
      } else {
        enqueueSnackbar(`Failed to fetch property content: ${JSON.stringify(response)}`, {
          variant: 'error',
        });
      }
    } catch (error) {
      enqueueSnackbar(`Failed to fetch property content: ${error.message}`, { variant: 'error' });
    }
  };

  const showGenerateContentModal = (contentType: InitiateContentGenerationRequest['contentType']) => {
    setContentTypeToGenerate(contentType);
    setIsGenerateContentModalOpen(true);
  };

  const initiateContentGeneration = (params: InitiateContentGenerationRequest) => {
    return initiateOfferContentGeneration({
      offerId: connectedOffer?.idSalesforceExternal ?? '',
      ...params,
    });
  };

  return (
    <>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" align="center">
              {hasGeneratedContent() ? 'Generated Content' : 'No generated content'}
            </Typography>
            <Stack direction="row" spacing={1}>
              <PermissionedComponent requiredRoles={CONTENT_ROLES}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => showGenerateContentModal('description')}
                  disabled={!canGenerateContent}
                >
                  {hasGeneratedContent() ? 'Regenerate Description' : 'Generate Description'}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => showGenerateContentModal('nearby')}
                  disabled={!canGenerateContent}
                >
                  {hasGeneratedContent() ? 'Regenerate Nearby' : 'Generate Nearby'}
                </Button>
              </PermissionedComponent>
            </Stack>
          </Stack>
          {hasGeneratedContent() && (
            <PermissionedComponent requiredRoles={CONTENT_ROLES}>
              <AccommodationGeneratedContentView content={propertyContent.generatedContents} />
            </PermissionedComponent>
          )}
        </Grid>
      </Grid>

      {isGenerateContentModalOpen && (
        <GenerateContentModal
          onClose={() => setIsGenerateContentModalOpen(false)}
          initiateContentGeneration={initiateContentGeneration}
          fetchContent={fetchContent}
          includeOfferInGeneration={hasGeneratedContent()}
          setUseWebSearch={setUseWebSearch}
          useWebSearch={useWebSearch}
          contentType={contentTypeToGenerate}
        />
      )}
    </>
  );
}
